<template>
  <div class="mi-site mi-site--page">
    <a class="mi-skip" href="#main">Skip to content</a>
    <MarketingHeader />

    <main id="main" class="mi-dir">
      <div class="mi-hero-atmosphere" aria-hidden="true">
        <span class="mi-hero-orb mi-hero-orb-a"></span>
        <span class="mi-hero-orb mi-hero-orb-c"></span>
      </div>

      <div class="mi-dir-wide mi-dir-hero">
        <div>
          <p class="mi-kicker">
            <span class="mi-kicker-dot" aria-hidden="true"></span>
            RESO US list
          </p>
          <h1>MLS <span class="mi-grad">directory</span></h1>
          <p class="mi-lede mi-lede--left">
            Every US MLS in the RESO registry. Select a state, or search a board.
          </p>
        </div>
      </div>

      <div class="mi-dir-wide mi-dir-split">
        <div class="mi-dir-listcol">
          <div class="mi-dir-toolbar">
            <label class="mi-dir-search">
              <span class="mi-dir-search-label">Search</span>
              <input
                v-model.trim="query"
                type="search"
                placeholder="Board, city, or acronym"
                autocomplete="off"
              />
            </label>
            <button
              v-if="selected || query"
              type="button"
              class="mi-ghost mi-dir-clear"
              @click="clear"
            >
              All states
            </button>
          </div>

          <p v-if="selectedLabel" class="mi-dir-filter">
            {{ selectedLabel }}
            <span>{{ filteredCount }}</span>
          </p>

          <p v-if="!groups.length" class="mi-dir-empty">No matching boards.</p>
          <section
            v-for="group in groups"
            :id="group.state"
            :key="group.state"
            class="mi-dir-state"
          >
            <h2>
              {{ group.name }}
              <span>{{ group.boards.length }}</span>
            </h2>
            <ul>
              <li
                v-for="board in group.boards"
                :key="board.key"
                :class="{ 'is-on': hoverBoard === board.key }"
                @mouseenter="hoverBoard = board.key"
                @mouseleave="hoverBoard = null"
              >
                <button type="button" class="mi-dir-row" @click="selectState(board.state, true)">
                  <span
                    class="mi-dir-status"
                    :class="{ 'is-live': board.live }"
                    aria-hidden="true"
                  />
                  <span class="mi-dir-row-copy">
                    <span class="mi-dir-board">{{ board.label }}</span>
                    <span class="mi-dir-row-meta">
                      <span v-if="board.city">{{ board.cityLabel }}</span>
                      <span v-if="board.acronym" class="mi-dir-acr">{{ board.acronym }}</span>
                    </span>
                  </span>
                  <span v-if="board.live" class="mi-dir-live">Live</span>
                </button>
              </li>
            </ul>
          </section>
        </div>

        <div class="mi-dir-mapcol">
          <div
            class="mi-dir-stage"
            @mousemove="onMapMove"
            @mouseleave="onMapLeave"
          >
            <svg
              ref="mapSvg"
              class="mi-dir-svg"
              viewBox="0 0 959 593"
              role="img"
              aria-label="United States map of MLS boards"
              @click="onMapClick"
            >
              <g class="mi-dir-land">
                <path
                  v-for="item in mapPaths"
                  :key="item.key"
                  :d="item.d"
                  :data-state="item.state"
                  :class="{
                    'is-empty': !counts[item.state],
                    'is-hover': hover === item.state,
                    'is-on': selected === item.state,
                  }"
                  :style="{ fill: fillFor(item.state) }"
                />
              </g>
              <g class="mi-dir-pins">
                <g
                  v-for="dot in dots"
                  :key="dot.key"
                  class="mi-dir-pin"
                  :class="{
                    'is-live': dot.live,
                    'is-on': hoverBoard === dot.key,
                    'is-dim': isDotDim(dot),
                  }"
                  :transform="'translate(' + dot.x + ' ' + dot.y + ')'"
                  @mouseenter.stop="hoverBoard = dot.key"
                  @mouseleave="hoverBoard = null"
                  @click.stop="selectState(dot.state, true)"
                >
                  <circle class="mi-dir-hit" r="8" />
                  <circle class="mi-dir-core" r="3.1" />
                </g>
              </g>
            </svg>
            <div
              v-if="tipTitle"
              class="mi-dir-tip"
              :style="tipStyle"
            >
              <strong>{{ tipTitle }}</strong>
              <span>{{ tipMeta }}</span>
            </div>
          </div>
          <p class="mi-dir-legend">
            <span class="mi-dir-legend-live"></span> Live
            <span class="mi-dir-legend-dot"></span> Registry
          </p>
        </div>
      </div>
    </main>

    <MarketingFooter
      note="Directory from the RESO US MLS list. Live means an active feed on this network — not every board is ingested yet."
    />
  </div>
</template>

<script>
import MarketingHeader from './MarketingHeader'
import MarketingFooter from './MarketingFooter'
import directory from '../data/mlsDirectory.json'
import usMapPaths from '../data/usMapPaths.json'
import { US_STATE_NAMES, US_STATE_ORDER } from '../lib/usStates'
import '../assets/marketing.css'

function decode(value) {
  return String(value || '')
    .replace(/&amp;/g, '&')
    .replace(/&nbsp;/g, ' ')
    .replace(/&ndash;/g, '–')
    .replace(/&mdash;/g, '—')
    .replace(/&rsquo;/g, '’')
    .replace(/&lsquo;/g, '‘')
    .replace(/&rdquo;/g, '”')
    .replace(/&ldquo;/g, '“')
    .replace(/&#39;/g, "'")
}

function hashCode() {
  if (typeof window === 'undefined') return ''
  return (window.location.hash || '').replace('#', '').toUpperCase()
}

function matches(board, q) {
  if (!q) return true
  const hay = [board.label, board.acronym, board.cityLabel, board.state, US_STATE_NAMES[board.state]]
    .join(' ')
    .toLowerCase()
  return hay.includes(q)
}

export default {
  name: 'MlsDirectoryPage',
  components: { MarketingHeader, MarketingFooter },
  data() {
    const code = hashCode()
    return {
      query: '',
      selected: US_STATE_NAMES[code] ? code : null,
      hover: null,
      hoverBoard: null,
      tip: { x: 0, y: 0 },
      dots: [],
      boards: directory.boards.map((board, i) => ({
        ...board,
        key: board.state + '-' + i,
        label: decode(board.name),
        cityLabel: decode(board.city),
      })),
      mapPaths: Object.keys(usMapPaths).reduce((list, state) => {
        usMapPaths[state].forEach((d, i) => {
          list.push({ key: state + '-' + i, state, d })
        })
        return list
      }, []),
    }
  },
  computed: {
    counts() {
      const map = {}
      this.boards.forEach((b) => {
        map[b.state] = (map[b.state] || 0) + 1
      })
      return map
    },
    hoverLabel() {
      return this.hover ? US_STATE_NAMES[this.hover] : ''
    },
    selectedLabel() {
      return this.selected ? US_STATE_NAMES[this.selected] : ''
    },
    activeDot() {
      if (!this.hoverBoard) return null
      return this.dots.find((dot) => dot.key === this.hoverBoard) || null
    },
    tipTitle() {
      if (this.activeDot) return this.activeDot.label
      return this.hoverLabel
    },
    tipMeta() {
      if (this.activeDot) {
        const city = this.activeDot.cityLabel
        const live = this.activeDot.live ? 'Live' : 'Registry'
        return city ? city + ' · ' + live : live
      }
      if (!this.hover) return ''
      const n = this.counts[this.hover] || 0
      return n + ' MLS'
    },
    tipStyle() {
      return {
        left: this.tip.x + 14 + 'px',
        top: this.tip.y + 12 + 'px',
      }
    },
    groups() {
      const q = this.query.toLowerCase()
      const byState = {}
      this.boards.forEach((b) => {
        if (this.selected && b.state !== this.selected) return
        if (!matches(b, q)) return
        if (!byState[b.state]) byState[b.state] = []
        byState[b.state].push(b)
      })
      return US_STATE_ORDER.filter((code) => byState[code] && byState[code].length).map((code) => ({
        state: code,
        name: US_STATE_NAMES[code],
        boards: byState[code],
      }))
    },
    filteredCount() {
      return this.groups.reduce((n, group) => n + group.boards.length, 0)
    },
  },
  watch: {
    '$route.hash'() {
      this.syncFromHash()
    },
  },
  mounted() {
    this.syncFromHash()
    this.$nextTick(this.placeDots)
  },
  methods: {
    fillFor(state) {
      const n = this.counts[state] || 0
      if (!n) return '#f0f0f2'
      if (this.selected === state) return '#ffedd5'
      if (this.hover === state) return '#e7e7ea'
      return '#ececee'
    },
    isDotDim(dot) {
      if (this.selected && dot.state !== this.selected) return true
      if (this.query && !matches(dot, this.query.toLowerCase())) return true
      return false
    },
    hashFromRoute() {
      return (this.$route.hash || '').replace('#', '').toUpperCase()
    },
    syncFromHash() {
      const code = this.hashFromRoute()
      this.selected = US_STATE_NAMES[code] ? code : null
    },
    setHash(state) {
      const next = state ? '#' + state : ''
      if ((this.$route.hash || '') !== next) {
        this.$router.replace({ path: '/mls-directory', hash: next })
      }
    },
    selectState(state, keep) {
      if (!state || !US_STATE_NAMES[state] || !this.counts[state]) return
      this.selected = !keep && this.selected === state ? null : state
      this.setHash(this.selected)
    },
    onMapClick(event) {
      const path = event.target.closest && event.target.closest('path[data-state]')
      const state = path && path.getAttribute('data-state')
      this.selectState(state)
    },
    onMapMove(event) {
      const pin = event.target.closest && event.target.closest('.mi-dir-pin')
      if (!pin) {
        const path = event.target.closest && event.target.closest('path[data-state]')
        const state = path && path.getAttribute('data-state')
        this.hover = state && US_STATE_NAMES[state] ? state : null
      }
      const box = event.currentTarget.getBoundingClientRect()
      this.tip = { x: event.clientX - box.left, y: event.clientY - box.top }
    },
    onMapLeave() {
      this.hover = null
      this.hoverBoard = null
    },
    clear() {
      this.selected = null
      this.query = ''
      this.setHash('')
    },
    placeDots() {
      const svg = this.$refs.mapSvg
      if (!svg || typeof svg.querySelectorAll !== 'function') return
      const boxes = {}
      const nodes = svg.querySelectorAll('g.mi-dir-land path[data-state]')
      for (let i = 0; i < nodes.length; i++) {
        const el = nodes[i]
        const state = el.getAttribute('data-state')
        const b = el.getBBox()
        const area = b.width * b.height
        if (!boxes[state] || area > boxes[state].area) {
          boxes[state] = { x: b.x, y: b.y, width: b.width, height: b.height, area: area }
        }
      }
      const index = {}
      this.dots = this.boards.map((board) => {
        const box = boxes[board.state]
        if (!box) return Object.assign({}, board, { x: 0, y: 0 })
        const n = Math.max(1, this.counts[board.state])
        const i = index[board.state] || 0
        index[board.state] = i + 1
        const golden = Math.PI * (3 - Math.sqrt(5))
        const t = (i + 0.55) / n
        const radius = Math.sqrt(t) * Math.min(box.width, box.height) * 0.32
        const a = i * golden
        return Object.assign({}, board, {
          x: box.x + box.width / 2 + Math.cos(a) * radius,
          y: box.y + box.height / 2 + Math.sin(a) * radius * 0.86,
        })
      })
    },
  },
}
</script>
